import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Eyebrow, Btn } from "../components/Primitives";
import ShareArchetypeCard from "../components/ShareArchetypeCard";
import { trackOnce } from "../lib/analytics";

export default function PartialReveal({ archetype, user, onUnlockPaid, onUnlockReferral }) {
    useEffect(() => {
        if (archetype) trackOnce("archetype_viewed", { archetype_id: archetype.id });
    }, [archetype]);

    if (!archetype) return null;

    return (
        <div className="flex-1 flex flex-col bg-cream relative" data-testid="partial-reveal-screen">
            <div className="px-7 pt-12 pb-8">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep font-sans">
                        Your archetype
                    </span>
                    <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[1.05] text-burgundy tracking-tight">
                        <span className="italic">{archetype.name.split(" ").slice(0, 2).join(" ")}</span>{" "}
                        {archetype.name.split(" ").slice(2).join(" ")}
                    </h1>
                    <p className="mt-5 font-display italic text-xl text-ink leading-snug">
                        {archetype.tagline}
                    </p>
                    <div className="mt-6 editorial-rule" />
                </motion.div>

                {user?.referral_code && (
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6"
                    >
                        <ShareArchetypeCard archetype={archetype} referralCode={user.referral_code} />
                    </motion.div>
                )}
            </div>

            {/* Blurred locked content */}
            <div className="relative px-7 pb-40" data-testid="locked-content">
                <div className="blur-content space-y-7">
                    <section>
                        <Eyebrow>Your full profile</Eyebrow>
                        <p className="mt-3 font-display text-xl leading-relaxed text-ink whitespace-pre-line">
                            {archetype.full_description}
                        </p>
                    </section>
                    <section>
                        <Eyebrow>Your ideal partner</Eyebrow>
                        <p className="mt-3 font-display text-2xl text-burgundy">
                            The Steady Anchor
                        </p>
                    </section>
                    <section>
                        <Eyebrow>Why you keep attracting this pattern</Eyebrow>
                        <p className="mt-3 font-display text-xl leading-relaxed text-ink">
                            {archetype.attracting_pattern}
                        </p>
                    </section>
                    <section>
                        <Eyebrow>Your compatibility blind spots</Eyebrow>
                        <ul className="mt-3 space-y-2 font-display text-lg text-ink">
                            {archetype.blind_spots.map((b) => (
                                <li key={b}>— {b}</li>
                            ))}
                        </ul>
                    </section>
                </div>

                {/* Fade to cream over bottom of blurred content */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-cream via-cream/85 to-transparent" />
            </div>

            {/* Sticky CTAs */}
            <div className="sticky bottom-0 left-0 right-0 bg-cream/95 backdrop-blur-md border-t border-burgundy/10 px-6 pt-5 pb-safe-sm space-y-3 z-30">
                <p className="text-center text-[10px] tracking-[0.32em] uppercase text-ink-muted pb-1">
                    Locked — unlock to read in full
                </p>
                <Btn data-testid="unlock-paid-btn" onClick={onUnlockPaid}>
                    Unlock my full profile
                </Btn>
                <Btn data-testid="unlock-referral-btn" variant="secondary" onClick={onUnlockReferral}>
                    Invite 3 friends to unlock
                </Btn>
            </div>
        </div>
    );
}
